import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import type { IShopper } from '@makemydeal/dr-platform-types';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useValidate } from '../hooks/useValidate';
import {
    openDeal,
    syncConsumerData,
    toggleChangeVehicleEditView,
    updateCommonConsumer,
    updateCommonConsumerValidationState,
    updateDealership
} from '../store';
import type { Selectors } from '../store/selectors';
import type { CustomerCardFormValues } from '@makemydeal/dr-dash-types';
import * as dealerActionsCreators from '@makemydeal/dr-dash-store/actions/dealerActionsCreators';

export const useOpenDeal = (selectors: Selectors) => {
    const dispatch = useDispatch();
    const availableDealers = useSelector(selectors.getDealers);
    const saveDealStatus = useSelector(selectors.getSaveDealStatus);
    const dealer = useSelector(selectors.getSelectedDealer);
    const incompleteDealDetails = useSelector(selectors.getIncompleteDealDetails);
    const selectedDealerId = !incompleteDealDetails?.missingDealership ? `${dealer?.dealerId}` : '';
    const { isDealValid, isVehicleValid } = useValidate();

    const onDealershipSelect = useCallback(
        (e: InterstateOnChangeEvent<SelectInputEventValue>) => {
            const selectedDealerId = e.target.value;
            const selectedDealer = availableDealers.find((dealer) => `${dealer.dealerMMDId}` === selectedDealerId);
            if (selectedDealer) {
                // TODO: We need to use dealerId instead of dealerMMDID
                selectedDealer.dealerId = selectedDealer.dealerMMDId;
                dispatch(updateDealership(selectedDealer));
                // This action is updating the dealer in the global state as based on OpenDealV2 Dealership component
                dispatch(dealerActionsCreators.updateDealer(selectedDealer));
            }
        },
        [availableDealers]
    );

    const onCustomerCardValidationChange = (isValid: boolean, data?: CustomerCardFormValues) => {
        dispatch(updateCommonConsumerValidationState(isValid));
        if (data) {
            onConsumerSyncSuccess(data as IShopper);
        }
    };

    const onConsumerSyncSuccess = (data: IShopper) => {
        dispatch(updateCommonConsumer(data));
        dispatch(openDeal());
        dispatch(syncConsumerData(false));
    };

    const handleOpenDeal = () => {
        dispatch(syncConsumerData(true));
    };

    const onEditModeChange = () => {
        dispatch(toggleChangeVehicleEditView());
    };

    useEffect(() => {
        if (!isVehicleValid) {
            dispatch(toggleChangeVehicleEditView());
        }
    }, [isVehicleValid]);

    useEffect(() => {
        // Update global dealer state when selected dealer changes or available dealers are updated
        const selectedDealer = availableDealers.find((dealer) => `${dealer.dealerMMDId}` === selectedDealerId);
        if (selectedDealer)
            dispatch(dealerActionsCreators.updateDealer({ ...selectedDealer, dealerId: selectedDealer.dealerMMDId }));
    }, [availableDealers, selectedDealerId, dispatch]);

    return {
        onDealershipSelect,
        isDealValid,
        onCustomerCardValidationChange,
        handleOpenDeal,
        onEditModeChange,
        saveDealStatus
    };
};
